import { init, captureException, browserTracingIntegration, ErrorBoundary } from '@sentry/react'
import { scrubSentryBreadcrumb, scrubSentryEvent } from '@/lib/analytics-scrub'

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

// Browser-side init only. SSR runtime (Cloudflare Workers / Pages Functions)
// runs in workerd, which lacks some browser globals that @sentry/react expects.
// Server errors are captured separately by the Rust backend (uptrack-server) via
// the `sentry` + `sentry-tracing` crates (crates/core `observability::init`),
// gated on the SENTRY_DSN env — the successor to the legacy Elixir SDK on
// uptrack-api, which is retired with the Phoenix HTTP surface at cutover.

// Per-fingerprint sliding-window rate limiter, mirroring the backend
// (uptrack-server crates/core observability.rs). Sentry's free quota (5k
// errors/mo, billed per event and shared org-wide) is exhausted by a single
// repeating error firing on every render; we only need a few samples per
// distinct issue. Allow `cap` events per fingerprint per window, drop the rest.
// Runs after Sentry's own ignoreErrors/denyUrls filters, so noise never
// consumes an allowance. Fail-open.
type RateLimitEvent = {
  exception?: { values?: Array<{ type?: string; value?: string }> }
  message?: string
  level?: string
  transaction?: string
}
const RL_BURST = Number((import.meta.env.VITE_SENTRY_RL_BURST as string | undefined) ?? '5')
const RL_SUSTAIN = Number((import.meta.env.VITE_SENTRY_RL_SUSTAIN as string | undefined) ?? '5')
const RL_WINDOW_MS =
  Number((import.meta.env.VITE_SENTRY_RL_WINDOW_SECS as string | undefined) ?? '3600') * 1000
const RL_CAP = Math.max(1, (RL_BURST || 0) + (RL_SUSTAIN || 0))
const rlState = new Map<string, { count: number; windowStart: number }>()

function fingerprintKey(event: RateLimitEvent): string {
  const exc = event.exception?.values?.[0]
  if (exc) return `exc:${exc.type ?? ''}:${exc.value ?? ''}`
  if (event.message) return `msg:${event.message}`
  return `lvl:${event.level ?? ''}:${event.transaction ?? ''}`
}

function rateLimitAllow(event: RateLimitEvent): boolean {
  try {
    const now = Date.now()
    const key = fingerprintKey(event)
    let e = rlState.get(key)
    if (!e || now - e.windowStart >= RL_WINDOW_MS) {
      e = { count: 0, windowStart: now }
      rlState.set(key, e)
    }
    e.count += 1
    if (rlState.size > 2048) {
      for (const [k, v] of rlState) {
        if (now - v.windowStart >= RL_WINDOW_MS) rlState.delete(k)
      }
    }
    return e.count <= RL_CAP
  } catch {
    return true // fail open — never lose an event to a limiter bug
  }
}

let initialized = false
if (dsn && typeof window !== 'undefined' && !initialized) {
  init({
    dsn,
    environment: (import.meta.env.MODE as string | undefined) ?? 'production',
    release: (import.meta.env.VITE_APP_REVISION as string | undefined) ?? undefined,
    // Trace 5% of transactions by default — performance units are plan-limited
    // too. Bump locally with VITE_SENTRY_TRACES_SAMPLE_RATE if debugging perf.
    tracesSampleRate: Number(
      (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) ?? '0.05',
    ),
    // Quota guard: drop repeats of the same issue past the per-fingerprint cap
    // so one error firing on every render can't drain the shared monthly quota.
    beforeSend(event) {
      if (!rateLimitAllow(event)) {
        if (typeof console !== 'undefined') {
          console.debug('[sentry] event rate-limited (quota guard)')
        }
        return null
      }
      // The page URL is in every event: keep the path only (the magic-link page has a token in it).
      return scrubSentryEvent(event)
    },
    beforeBreadcrumb: (crumb) => scrubSentryBreadcrumb(crumb),
    // Replay is off by default — opt in per session if needed.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      browserTracingIntegration(),
    ],
    // No default PII (cookies, IP). Query strings are removed in beforeSend and beforeBreadcrumb.
    sendDefaultPii: false,
    // Known noise from third-party crawlers / browser extensions / SaaS bots
    // that hit our pages but aren't real bugs. Extend cautiously — over-broad
    // patterns suppress legit errors.
    ignoreErrors: [
      // Microsoft Outlook SafeLinks crawler (Azure Boydton VA) — fires this
      // exact promise rejection on every link preview. ~all clicks from
      // Outlook produce one.
      'Object Not Found Matching Id:',
      // Chromium-based browser extensions occasionally inject this when their
      // content scripts can't reach their background page.
      /extension context invalidated/i,
      // ResizeObserver loop limit reached — benign browser glitch, no impact.
      /ResizeObserver loop/i,
    ],
    denyUrls: [
      // Browser extension origins; we never ship code from these.
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
    ],
  })
  initialized = true
}

export const sentryEnabled = initialized

// Drop-in replacement for the previous AppSignal `reportError` API so callers
// don't have to change.
export function reportError(error: unknown, tags?: Record<string, string>) {
  if (!initialized) return
  const err = error instanceof Error ? error : new Error(String(error))
  captureException(err, tags ? { tags } : undefined)
}

export { ErrorBoundary }
